import { createFileRoute, Link } from "@tanstack/react-router";
import { Clock, RefreshCcw, CalendarCheck, CreditCard, MessageCircle, ShieldCheck } from "lucide-react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { SITE } from "@/lib/site";
import { cancellationPolicy } from "@/lib/cancellation-policy";

export const Route = createFileRoute("/cancellation")({
  head: () => ({
    meta: [
      { title: `Política de cancelación — ${SITE.brand.name}` },
      {
        name: "description",
        content:
          "Cambio de fecha gratis hasta 24 h antes; con más de 24 h, reembolso del total menos 5% de cargo administrativo; por clima u operador, reembolso total o cambio de fecha.",
      },
      { property: "og:title", content: `Política de cancelación — ${SITE.brand.name}` },
      { property: "og:url", content: "/cancellation" },
      { property: "og:description", content: "Cambio de fecha gratis hasta 24 h antes y reembolso con más de 24 h menos 5% de cargo administrativo." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://www.mostrandomexico.mx/cancellation" }],
  }),
  component: CancellationPage,
});

const highlights = [
  { icon: CalendarCheck, title: "Cambio de fecha gratis", desc: "Hasta 24 h antes de tu experiencia, sujeto a disponibilidad." },
  { icon: RefreshCcw, title: "Reembolso con más de 24 h", desc: "Devolvemos el total menos 5% de cargo administrativo." },
  { icon: Clock, title: "Menos de 24 h o no presentarse", desc: "No hay reembolso." },
  { icon: MessageCircle, title: "Clima u operador", desc: "Si el operador cancela o el clima lo impide, eliges reembolso total o cambio de fecha." },
];

function CancellationPage() {
  const waUrl = SITE.contact.whatsappPrettyUrl;
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
        <header className="mb-12 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Confianza al reservar</p>
          <h1 className="mt-3 font-display text-4xl font-semibold sm:text-5xl">
            Política de cancelación
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base text-muted-foreground">
            Esta política aplica a todas las reservas. Si la página de un tour muestra condiciones más
            flexibles para ti, aplican esas.
          </p>
        </header>

        <section className="grid gap-4 sm:grid-cols-2">
          {highlights.map((h) => (
            <article
              key={h.title}
              className="rounded-2xl border border-border bg-card p-6 shadow-card"
            >
              <span className="grid h-10 w-10 place-items-center rounded-full bg-primary/10 text-primary">
                <h.icon className="h-5 w-5" aria-hidden="true" />
              </span>
              <h2 className="mt-4 font-display text-lg font-semibold text-foreground">{h.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{h.desc}</p>
            </article>
          ))}
        </section>

        <section className="mt-12 space-y-8 rounded-3xl border border-border bg-card p-8 shadow-card">
          <div>
            <h2 className="font-display text-2xl font-semibold">Cómo funciona</h2>
            <ol className="mt-4 space-y-3 text-sm leading-relaxed text-muted-foreground">
              <li>
                <strong className="text-foreground">1. Solicita la cancelación.</strong> Escríbenos por
                WhatsApp al {SITE.contact.phone}, llámanos o envía un correo a {" "}
                <a className="text-primary underline" href={`mailto:${SITE.contact.email}`}>
                  {SITE.contact.email}
                </a>{" "}
                con tu número de reserva.
              </li>
              <li>
                <strong className="text-foreground">2. Confirmamos en minutos.</strong> Nuestro equipo
                valida tu reserva y te confirma el reembolso o el cambio de fecha.
              </li>
              <li>
                <strong className="text-foreground">3. Recibe el reembolso.</strong> Cuando aplique, el monto
                se devuelve al mismo método de pago (el tiempo de acreditación depende de tu banco).
              </li>
            </ol>
          </div>

          <div>
            <h2 className="font-display text-2xl font-semibold">Condiciones</h2>
            <ul className="mt-4 list-disc space-y-2 pl-5 text-sm leading-relaxed text-muted-foreground">
              {cancellationPolicy("es").map((l) => (
                <li key={l}>{l}</li>
              ))}
              <li>Si la página del tour indica una política más flexible para el viajero, prevalece esa.</li>
            </ul>
          </div>

          <div>
            <h2 className="font-display text-2xl font-semibold">Métodos de pago aceptados</h2>
            <p className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
              <CreditCard className="h-4 w-4 text-primary" aria-hidden="true" />
              {SITE.policies.payments.join(" · ")}
            </p>
          </div>

          <div className="rounded-2xl bg-surface p-6">
            <h2 className="flex items-center gap-2 font-display text-xl font-semibold">
              <ShieldCheck className="h-5 w-5 text-primary" aria-hidden="true" />
              ¿Necesitas cancelar o reagendar?
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Nuestro equipo de atención al cliente está disponible 24/7 para ayudarte.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <a
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.02]"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp {SITE.contact.phone}
              </a>
              <a
                href={`mailto:${SITE.contact.email}`}
                className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-5 py-2.5 text-sm font-semibold text-foreground hover:bg-accent"
              >
                {SITE.contact.email}
              </a>
              <Link
                to="/help"
                className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-5 py-2.5 text-sm font-semibold text-foreground hover:bg-accent"
              >
                Centro de ayuda
              </Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
